import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
    dealsBoardQueryKeys,
    type TDealsBoard,
    updateDealInBoard,
} from '@crm/entities/deals-board';
import { keysToCamel } from '@crm/shared/lib';

import { dealApi } from './api-handlers';
import { dealQueryKeys } from './query-keys';

import type { TDealDetails } from '../model/deal-details';

export const useUpdateDealDetailsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: dealQueryKeys.updateDetails(),
        mutationFn: dealApi.updateDealDetails,
        onMutate: async ({ dealId, payload }) => {
            const detailQueryKey = dealQueryKeys.detail(dealId);
            const boardQueryKey = dealsBoardQueryKeys.board();

            await Promise.all([
                queryClient.cancelQueries({ queryKey: detailQueryKey }),
                queryClient.cancelQueries({ queryKey: boardQueryKey }),
            ]);

            const previousDealDetails =
                queryClient.getQueryData<TDealDetails>(detailQueryKey);
            const previousDealsBoard = queryClient.getQueryData<TDealsBoard>(boardQueryKey);
            const optimisticDealDataPatch = keysToCamel(payload) as Partial<
                TDealDetails['dealData']
            >;

            queryClient.setQueryData<TDealDetails>(detailQueryKey, (currentDealDetails) => {
                if (!currentDealDetails) {
                    return currentDealDetails;
                }

                return {
                    ...currentDealDetails,
                    dealData: {
                        ...currentDealDetails.dealData,
                        ...optimisticDealDataPatch,
                    },
                };
            });
            queryClient.setQueryData<TDealsBoard>(boardQueryKey, (currentDealsBoard) =>
                updateDealInBoard(currentDealsBoard, dealId, optimisticDealDataPatch)
            );

            return { previousDealDetails, previousDealsBoard };
        },
        onError: (_error, { dealId }, context) => {
            if (context?.previousDealDetails) {
                queryClient.setQueryData(dealQueryKeys.detail(dealId), context.previousDealDetails);
            }
            if (context?.previousDealsBoard) {
                queryClient.setQueryData(dealsBoardQueryKeys.board(), context.previousDealsBoard);
            }

        },
        onSuccess: async (dealDetailsData, { dealId }) => {
            queryClient.setQueryData(dealQueryKeys.detail(dealId), dealDetailsData);
            queryClient.setQueryData<TDealsBoard>(
                dealsBoardQueryKeys.board(),
                (currentDealsBoard) =>
                    updateDealInBoard(currentDealsBoard, dealId, dealDetailsData.dealData)
            );
            await queryClient.invalidateQueries({ queryKey: dealsBoardQueryKeys.board() });
        },
    });
};
